<template>
  <div class="mayor-map">
      <div class="mayor-map__map" ref="map"></div>
  </div>
</template>

<script>
import * as d3 from 'd3'
import * as topojson from 'topojson'
import taiwanTopoData2010 from '@/scripts/taiwan-2010.topo'
import taiwanTopoData2010Merge from '@/scripts/taiwan-2010merge.topo'

const SVG_CONFIG = {
  viewBox: '0 0 360 560',
  preserveAspectRatio: 'xMidYMid meet',
  width: 360,
  height: 560,
  scaleExtent: [1, 3],
  translateExtent: [[0, 0], [360, 560]]
}

const GEO_CONFIG = {
  center: [123.05, 24.3],
  scale: 5800
}

export default {
  name: 'MayorMap',
  inject: [ 'isDesktop' ],
  data () {
    return {
      TOPO_2010: taiwanTopoData2010,
      TOPO_2010_MERGE: taiwanTopoData2010Merge,
      geoData: null,
      projection: null,
      geoPath: null,
      svg: null,
      map: null,
      land: null,
      zoom: null,
      selectedId: null,
      zoomMode: false,
      zoomModeListener: null,
      zoomOffsetHeight: 200
    }
  },
  props: {
    value: { type: String, default: '' },
    year: { type: Number, default: 2010 },
    names: { type: Array, default: () => [] },
    colors: { type: Array, default: () => [] }
  },
  computed: {
    topoForYear () {
      return this.year >= 2010 ? this.TOPO_2010_MERGE : this.TOPO_2010
    }
  },
  watch: {
    colors (value, lastValue) {
      this.drawLand()
    },
    value (value, lastValue) {
      if (value !== this.selectedId) {
        this.areaZoom(value)
      }
    }
  },
  methods: {
    onZoom () {
      let transform = d3.event.transform
      let { k } = transform
      this.land.selectAll('path')
        .attr('stroke-width', 1 / k)
      this.map.attr('transform', transform)
    },
    drawLand () {
      let enter = enter => {
        enter
          .append('path')
          .attr('d', data => {
            return this.geoPath(data)
          })
          .attr('class', (d, i) => {
            return d.properties.id
          })
          .style('fill', '#ffffff')
          .style('fill', data => {
            let { name } = data.properties
            let colorIndex = this.names.indexOf(name)
            if (colorIndex < 0) {
              colorIndex = -1
            }
            return this.colors[colorIndex]
          })
          .style('stroke-linejoin', 'round')
          .style('stroke-linecap', 'round')
          .on('click', (d) => {
            let { id } = d.properties
            this.areaZoom(id)
          })
        if (this.isDesktop) {
          enter
            .selectAll('path')
            .on('mouseover', (d, i) => {
              let { id } = d.properties
              if (this.zoomMode && id === this.selectedId) {
                this.nonreactiveColorSet()
                this.reactiveColorRespond(id)
              } else {
                this.nonreactiveColorSet()
                this.land.selectAll(`.${id}`)
                  .transition()
                  .duration(100)
                  .style('fill', '#1a1a1a')
              }
            })
            .on('mouseout', (d, i) => {
              if (this.zoomMode) {
                this.nonreactiveColorSet()
                this.reactiveColorRespond(this.selectedId)
              } else {
                this.nonreactiveColorSet()
                this.reactiveColorRespond()
              }
            })
        }
      }

      let update = update => {
        update
          .attr('d', data => {
            // console.log(data)
            return this.geoPath(data)
          })
      }

      let exit = exit => {
        exit.remove()
      }

      this.land
        .selectAll('path')
        .data(this.geoData.features)
        .join(enter, update, exit)
    },
    areaZoom (id) {
      this.zoomMode = true

      let selectedPath = this.geoData.features.filter((item, index, array) => {
        return item.properties.id === id
      })

      if (!id || this.selectedId === id) {
        return this.areaReset()
      }

      let x, y, dx, dy, scale, bounds, translate
      // bounds = this.geoPath.bounds(selectedPath[0])
      bounds = selectedPath.reduce((bounds, path) => {
        let tmp = this.geoPath.bounds(path)
        let obj = {
          p1: { x: tmp[0][0], y: tmp[0][1] },
          p2: { x: tmp[1][0], y: tmp[1][1] }
        }
        if (!bounds.p1 || !bounds.p2) {
          return obj
        }
        const p1 = { x: tmp[0][0], y: tmp[0][1] }
        const p2 = { x: tmp[1][0], y: tmp[1][1] }
        const xMin = Math.min(bounds.p1.x, p1.x)
        const yMin = Math.min(bounds.p1.y, p1.y)
        const xMax = Math.max(bounds.p2.x, p2.x)
        const yMax = Math.max(bounds.p2.y, p2.y)
        return {
          p1: { x: xMin, y: yMin },
          p2: { x: xMax, y: yMax }
        }
      }, {})

      dx = bounds.p2.x - bounds.p1.x
      dy = bounds.p2.y - bounds.p1.y
      x = (bounds.p1.x + bounds.p2.x) / 2
      y = (bounds.p1.y + bounds.p2.y) / 2

      scale = 0.9 / Math.max(dx / SVG_CONFIG.width, dy / (SVG_CONFIG.height - this.zoomOffsetHeight))
      translate = [ (SVG_CONFIG.width / 2) - (scale * x), ((SVG_CONFIG.height - this.zoomOffsetHeight) / 2) - (scale * y) ]

      this.updateSelected(id)
      this.nonreactiveColorSet()
      this.reactiveColorRespond(this.selectedId)

      this.land
        .transition()
        .duration(750)
        .attr('transform', `translate(${translate}) scale(${scale})`)

      if (this.zoomMode) {
        this.zoomModeListener = this.$el.addEventListener('click', (e) => {
          if (e.target.nodeName !== 'path') {
            this.areaReset()
          }
        })
      }
    },
    areaReset (id) {
      this.zoomMode = false

      this.land.selectAll('path')
        .style('stroke', 'transparent')

      this.land.selectAll('path')
        .classed('active', false)

      this.updateSelected('')

      this.land
        .transition()
        .duration(750)
        .attr('transform', `translate(${SVG_CONFIG.width / 2}, ${SVG_CONFIG.height / 2}) scale(1) translate(${-SVG_CONFIG.width / 2}, ${-SVG_CONFIG.height / 2})`)

      if (!this.zoomMode) {
        this.$el.removeEventListener('click', this.zoomModeListener)
      }
    },
    updateSelected (id = '') {
      this.selectedId = id
      this.reactiveColorRespond(id)
      this.$emit('input', this.selectedId)
    },
    reactiveColorRespond (id) {
      let selectId
      if (id) {
        selectId = `.${id}`
      } else {
        selectId = 'path'
      }
      this.land.selectAll(selectId)
        .transition()
        .duration(100)
        .style('fill', data => {
          let { name } = data.properties
          let colorIndex = this.names.indexOf(name)
          if (colorIndex < 0) {
            colorIndex = -1
          }
          return this.colors[colorIndex]
        })
    },
    nonreactiveColorSet () {
      this.land.selectAll('path')
        .transition()
        .duration(100)
        .style('fill', '#bebebe')
    }
  },
  mounted () {
    this.geoData = topojson.feature(this.topoForYear, this.topoForYear.objects.taiwan)

    this.projection = d3
      .geoMercator()
      .center(GEO_CONFIG.center)
      .scale(GEO_CONFIG.scale)

    this.geoPath = d3.geoPath().projection(this.projection)

    this.svg = d3
      .select(this.$refs.map)
      .append('svg')
      .attr('viewBox', SVG_CONFIG.viewBox)
      // .attr('preserveAspectRatio', SVG_CONFIG.preserveAspectRatio)
      .attr('width', SVG_CONFIG.width)
      .attr('height', SVG_CONFIG.height)

    this.map = this.svg.append('g').classed('map', true)
    this.land = this.map.append('g').classed('land', true)

    // this.zoom = d3
    //   .zoom()
    //   .scaleExtent(SVG_CONFIG.scaleExtent)
    //   .translateExtent(SVG_CONFIG.translateExtent)
    //   .on('zoom', this.onZoom)
    // this.svg.call(this.zoom)
    this.drawLand()
    this.areaZoom(this.value)
  }
}
</script>

<style lang="scss">
.mayor-map {
  position: relative;
  display: block;
  max-width: 560px;
  margin: 0 auto;
  // outline: 1px solid red;

  &__map {
    display: block;
    position: relative;
    width: 100%;
    height: 0;
    padding-bottom: 400 /300 * 100%;
  }

  svg {
    position: absolute;
    width: 100%;
    height: 100%;
  }

  .map {
    position: relative;
    width: 100%;
    height: 100%;
  }

  path {
    cursor: pointer;
  }

  path.active {
    stroke-linejoin: round;
    stroke-linecap: round;
  }
}
</style>
